import React, { useEffect, useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { getEmailPreferences, setEmailPreferences, setEmailTypePreference, type EmailPrefs, type EmailType } from '../../services/emailService';

/**
 * Email notifications preferences — a MASTER subscribe switch plus per-category toggles (weekly summary,
 * comeback nudges, product updates). Subscribed by DEFAULT; this is the in-app counterpart to the one-click
 * unsubscribe link in the email footer. Loads current prefs, saves optimistically, reverts on failure. When the
 * master is off, the categories are disabled (nothing sends regardless).
 */
const CATEGORIES: Array<{ key: EmailType; label: string; blurb: string }> = [
  { key: 'weekly_summary', label: 'Weekly summary', blurb: 'A recap of what your specialists have been up to.' },
  { key: 'comeback', label: 'Occasional nudges', blurb: 'A friendly note when you have been away for a while.' },
  { key: 'product', label: 'Product updates', blurb: 'New features and improvements, now and then.' },
];

const Switch: React.FC<{ on: boolean; disabled?: boolean; onClick: () => void; label: string }> = ({ on, disabled, onClick, label }) => (
  <button
    type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} onClick={onClick}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 ${on ? 'bg-secondary-900 dark:bg-white' : 'bg-secondary-300 dark:bg-secondary-600'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-secondary-900 transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const EmailNotificationsSection: React.FC = () => {
  const [prefs, setPrefs] = useState<EmailPrefs>({ subscribed: true, types: { weekly_summary: true, comeback: true, product: true } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    getEmailPreferences()
      .then((p) => { if (alive && p) setPrefs({ subscribed: p.subscribed !== false, types: { weekly_summary: p.types?.weekly_summary !== false, comeback: p.types?.comeback !== false, product: p.types?.product !== false } }); })
      .catch(() => { /* default stays subscribed */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const flash = () => { setSaved(true); setTimeout(() => setSaved(false), 2000); };

  const toggleMaster = async () => {
    const next = !prefs.subscribed;
    const prev = prefs;
    setPrefs({ ...prefs, subscribed: next }); setSaving(true); setSaved(false);
    try { const p = await setEmailPreferences(next); setPrefs({ subscribed: p.subscribed !== false, types: p.types }); flash(); }
    catch { setPrefs(prev); }
    finally { setSaving(false); }
  };

  const toggleType = async (key: EmailType) => {
    const next = !prefs.types[key];
    const prev = prefs;
    setPrefs({ ...prefs, types: { ...prefs.types, [key]: next } }); setSaving(true); setSaved(false);
    try { const p = await setEmailTypePreference(key, next); setPrefs({ subscribed: p.subscribed !== false, types: p.types }); flash(); }
    catch { setPrefs(prev); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-lg border border-secondary-200 dark:border-secondary-700 p-6">
      <div className="flex items-center gap-2">
        <Mail className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Email notifications</h3>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-secondary-400" />}
        {saved && !saving && <Check className="w-3.5 h-3.5 text-green-500" />}
      </div>
      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
        Choose what lands in your inbox. No spam — unsubscribe any time, or turn off individual types below.
      </p>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm font-medium text-secondary-800 dark:text-secondary-100">
          {prefs.subscribed ? 'Email updates are on' : 'All email updates are off'}
        </span>
        <Switch on={prefs.subscribed} disabled={loading || saving} onClick={toggleMaster} label="Toggle all email notifications" />
      </div>

      <div className={`mt-4 space-y-3 border-t border-secondary-100 dark:border-secondary-700 pt-4 ${prefs.subscribed ? '' : 'opacity-50'}`}>
        {CATEGORIES.map((c) => (
          <div key={c.key} className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-secondary-800 dark:text-secondary-100">{c.label}</div>
              <div className="text-xs text-secondary-500 dark:text-secondary-400">{c.blurb}</div>
            </div>
            <Switch on={prefs.subscribed && prefs.types[c.key]} disabled={loading || saving || !prefs.subscribed} onClick={() => toggleType(c.key)} label={`Toggle ${c.label}`} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default EmailNotificationsSection;
